import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Phone,
  RefreshCw,
  Search,
  CheckCircle2,
  XCircle,
  X,
  Copy,
  Trash2,
  AlertTriangle,
  History,
} from "lucide-react";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Separator } from "../components/ui/separator";
import { Checkbox } from "../components/ui/checkbox";
import DateFilter from "../components/DateFilter";
import { SyncDataButton } from "../components/SyncDataButton";
import { CopyButton } from "../components/CopyField";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function formatDate(dateStr) {
  if (!dateStr || dateStr === "N/A" || dateStr.startsWith("0001")) return "N/A";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "N/A";
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch { return "N/A"; }
}

function daysSince(dateStr) {
  if (!dateStr || dateStr === "N/A" || dateStr.startsWith("0001")) return null;
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    return Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
  } catch { return null; }
}

function getInsightLabel(purchaseDate, totalCalls, status) {
  const days = daysSince(purchaseDate);
  if (days !== null && days < 7) return { label: "New", color: "bg-blue-500/15 text-blue-400 border-blue-500/30" };
  if (days !== null && days > 30 && totalCalls === 0 && status !== "Active") return { label: "Old & Dead", color: "bg-red-500/15 text-red-400 border-red-500/30" };
  if (days !== null && days >= 7 && days <= 30 && totalCalls === 0 && status !== "Active") return { label: "Idle", color: "bg-amber-500/15 text-amber-400 border-amber-500/30" };
  if (status === "Active" && totalCalls > 0) return { label: "Healthy", color: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" };
  if (status === "Active" && totalCalls === 0) return { label: "Low Activity", color: "bg-slate-500/15 text-slate-400 border-slate-500/30" };
  if (totalCalls === 0) return { label: "No Activity", color: "bg-slate-500/15 text-slate-400 border-slate-500/30" };
  return { label: "Healthy", color: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" };
}

const ITEMS_PER_PAGE = 25;

function CopyBtn({ text, label }) {
  return <CopyButton text={text} label={label} testId={`copy-${label?.toLowerCase().replace(/\s/g, "-")}`} />;
}

export default function DIDStatusPage() {
  const [dids, setDids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastSynced, setLastSynced] = useState(null);
  const [ringbaUnavailable, setRingbaUnavailable] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [campaignFilter, setCampaignFilter] = useState("all");
  const [ageFilter, setAgeFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedDid, setSelectedDid] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [dateFilter, setDateFilter] = useState(null);

  // Delete state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // single DID or "bulk"
  const [deleting, setDeleting] = useState(false);
  const [deletionLogOpen, setDeletionLogOpen] = useState(false);
  const [deletionLogs, setDeletionLogs] = useState([]);

  const fetchDids = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (dateFilter) {
        params.append("start_date", dateFilter.startDate);
        params.append("end_date", dateFilter.endDate);
      }
      const res = await axios.get(`${API}/did-status/data?${params}`);
      setDids(res.data.dids || []);
      setRingbaUnavailable(!!res.data.ringba_unavailable);
      setLastSynced(new Date().toISOString());
    } catch (err) {
      toast.error("Failed to load DID data");
    } finally {
      setLoading(false);
    }
  }, [dateFilter]);

  useEffect(() => { fetchDids(); }, [fetchDids]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/did-status/refresh`);
      toast.success("DID data synced");
      await fetchDids();
    } catch (err) {
      toast.error("Failed to refresh");
    } finally {
      setRefreshing(false);
    }
  };

  // Copy row info
  const copyRowInfo = async (did) => {
    const text = `DID: ${did.did_number}\nCampaign: ${did.campaign_name}\nPublisher: ${did.publisher_name}\nStatus: ${did.status}\nPurchase: ${formatDate(did.purchase_date)}\nLast Call: ${formatDate(did.last_call_date)}\nCalls: ${did.total_calls}`;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    toast.success("DID info copied to clipboard");
  };

  // Delete handlers
  const confirmSingleDelete = (did) => {
    setDeleteTarget(did);
    setDeleteConfirmOpen(true);
  };

  const confirmBulkDelete = () => {
    setDeleteTarget("bulk");
    setDeleteConfirmOpen(true);
  };

  const executeDelete = async () => {
    setDeleting(true);
    try {
      if (deleteTarget === "bulk") {
        const ids = Array.from(selectedIds);
        const res = await axios.post(`${API}/did-status/bulk-delete`, { number_ids: ids });
        const data = res.data;
        if (data.results?.failed?.length > 0) {
          const failCount = data.results.failed.length;
          const successCount = data.results.success.length;
          if (successCount > 0) toast.success(`${successCount} DIDs deleted`);
          if (data.results.failed.some(f => f.error?.includes("remove all references"))) {
            toast.error(`${failCount} DIDs still assigned to campaigns in Ringba. Unassign them in Ringba Dashboard first.`, { duration: 8000 });
          } else {
            toast.error(`${failCount} DIDs failed to delete`);
          }
        } else {
          toast.success(data.message);
        }
        setSelectedIds(new Set());
      } else {
        await axios.delete(`${API}/did-status/delete/${deleteTarget.ringba_id}`);
        toast.success(`${deleteTarget.did_number} deallocated from Ringba`);
      }
      setDeleteConfirmOpen(false);
      setDeleteTarget(null);
      await handleRefresh();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      if (detail.includes("still assigned to a campaign")) {
        toast.error(detail, { duration: 10000 });
      } else {
        toast.error("Delete failed: " + detail);
      }
    } finally {
      setDeleting(false);
    }
  };

  const fetchDeletionLog = async () => {
    try {
      const res = await axios.get(`${API}/did-status/deletion-log`);
      setDeletionLogs(res.data.logs || []);
      setDeletionLogOpen(true);
    } catch (err) {
      toast.error("Failed to load deletion log");
    }
  };

  // Selection
  const toggleSelect = (ringbaId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(ringbaId)) next.delete(ringbaId);
      else next.add(ringbaId);
      return next;
    });
  };

  const toggleSelectAllPage = () => {
    const pageIds = paginated.filter(d => d.status === "Non-Active").map(d => d.ringba_id);
    const allSelected = pageIds.every(id => selectedIds.has(id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      pageIds.forEach(id => allSelected ? next.delete(id) : next.add(id));
      return next;
    });
  };

  const campaigns = [...new Set(dids.map(d => d.campaign_name).filter(Boolean))].sort();

  const filtered = dids.filter(d => {
    if (search) {
      const q = search.toLowerCase();
      if (!d.did_number?.toLowerCase().includes(q) && !d.campaign_name?.toLowerCase().includes(q) && !d.publisher_name?.toLowerCase().includes(q)) return false;
    }
    if (statusFilter === "active" && d.status !== "Active") return false;
    if (statusFilter === "non-active" && d.status !== "Non-Active") return false;
    if (campaignFilter !== "all" && d.campaign_name !== campaignFilter) return false;
    if (ageFilter === "new") { const days = daysSince(d.purchase_date); if (days === null || days >= 7) return false; }
    if (ageFilter === "old") { const days = daysSince(d.purchase_date); if (days === null || days <= 30) return false; }
    return true;
  });

  const totalDids = filtered.length;
  const activeDids = filtered.filter(d => d.status === "Active").length;
  const nonActiveDids = filtered.filter(d => d.status === "Non-Active").length;
  const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  const paginated = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  useEffect(() => { setCurrentPage(1); }, [search, statusFilter, campaignFilter, ageFilter]);

  const activeFilters = [statusFilter !== "all", campaignFilter !== "all", ageFilter !== "all", search].filter(Boolean).length;
  const bulkSelectedDids = dids.filter(d => selectedIds.has(d.ringba_id));

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-[1600px] mx-auto" data-testid="did-status-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-white tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="did-status-title">DID Status</h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">{loading ? "Loading..." : `${totalDids} DIDs found`}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-[11px] font-semibold tracking-wide">EST Timezone</Badge>
          <button onClick={fetchDeletionLog} className="flex items-center gap-1.5 text-slate-500 hover:text-slate-300 rounded-md px-2.5 py-1.5 border border-white/10 text-xs transition-all" data-testid="deletion-log-btn">
            <History className="h-3.5 w-3.5" /> Deletion Log
          </button>
          <SyncDataButton onSync={handleRefresh} syncing={refreshing} lastSynced={lastSynced} ringbaUnavailable={ringbaUnavailable} testId="sync-data-btn" />
        </div>
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3 flex items-center justify-between" data-testid="bulk-action-bar">
          <div className="flex items-center gap-2 text-sm">
            <Trash2 className="h-4 w-4 text-red-400" />
            <span className="text-red-300 font-medium">{selectedIds.size} Non-Active DID{selectedIds.size > 1 ? "s" : ""} selected</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setSelectedIds(new Set())} className="text-xs text-slate-400 hover:text-white px-2 py-1 rounded transition-all">Clear</button>
            <button onClick={confirmBulkDelete} data-testid="bulk-delete-btn" className="flex items-center gap-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-md px-3 py-1.5 text-xs font-medium border border-red-500/30 transition-all">
              <Trash2 className="h-3.5 w-3.5" /> Delete Selected from Ringba
            </button>
          </div>
        </div>
      )}

      {/* Filters Card */}
      <div className="bg-[#11141D] border border-white/5 rounded-xl p-3 sm:p-4 space-y-3">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
          <div className="w-full sm:w-64 flex items-center gap-2 bg-[#0B0D12] border border-white/10 rounded-md px-3 h-9 focus-within:ring-1 focus-within:ring-blue-500/50 transition shrink-0">
            <Search className="h-3.5 w-3.5 text-slate-500 shrink-0" />
            <input type="text" placeholder="Search DID, campaign, publisher..." value={search} onChange={(e) => setSearch(e.target.value)} className="flex-1 bg-transparent text-sm text-white placeholder:text-slate-500 focus:outline-none" data-testid="did-search-input" />
          </div>
          <div className="flex-1 min-w-0">
            <DateFilter onFilterChange={setDateFilter} defaultPreset="last_30_days" compact showTimezone={false} />
          </div>
          <button onClick={fetchDids} disabled={loading} className="flex items-center justify-center gap-1.5 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg px-2.5 py-1.5 transition-all shrink-0">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            <span className="text-sm">Refresh</span>
          </button>
        </div>
        <div className="border-t border-white/5" />
        <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
          {[
            { key: "all", label: "All", activeClass: "bg-blue-600/15 border-blue-500/30 text-blue-400" },
            { key: "active", label: "Active", icon: CheckCircle2, activeClass: "bg-emerald-600/15 border-emerald-500/30 text-emerald-400" },
            { key: "non-active", label: "Non-Active", icon: XCircle, activeClass: "bg-red-600/15 border-red-500/30 text-red-400" },
          ].map((item) => {
            const isActive = statusFilter === item.key;
            const Icon = item.icon;
            const count = item.key === "all" ? totalDids : item.key === "active" ? activeDids : nonActiveDids;
            return (
              <button key={item.key} onClick={() => setStatusFilter(item.key)} data-testid={`filter-${item.key}`} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all whitespace-nowrap ${isActive ? item.activeClass : "border-white/10 text-slate-400 hover:bg-white/5 hover:text-slate-200"}`}>
                {Icon && <Icon className="h-3.5 w-3.5" />}
                {item.label}
                <span className={`ml-0.5 text-[10px] ${isActive ? "opacity-80" : "opacity-50"}`}>({count})</span>
              </button>
            );
          })}
          <div className="w-px h-5 bg-white/10 mx-1" />
          <Select value={campaignFilter} onValueChange={setCampaignFilter}>
            <SelectTrigger className="h-7 w-[160px] text-xs bg-transparent border-white/10 text-slate-400 hover:text-slate-200" data-testid="campaign-filter"><SelectValue placeholder="All Campaigns" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Campaigns</SelectItem>{campaigns.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={ageFilter} onValueChange={setAgeFilter}>
            <SelectTrigger className="h-7 w-[130px] text-xs bg-transparent border-white/10 text-slate-400 hover:text-slate-200" data-testid="age-filter"><SelectValue placeholder="All Ages" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Ages</SelectItem><SelectItem value="new">New (&lt;7 days)</SelectItem><SelectItem value="old">Old (&gt;30 days)</SelectItem></SelectContent>
          </Select>
          {activeFilters > 0 && (
            <><div className="w-px h-5 bg-white/10 mx-1" />
            <button onClick={() => { setStatusFilter("all"); setCampaignFilter("all"); setAgeFilter("all"); setSearch(""); }} className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-300 transition-colors whitespace-nowrap" data-testid="clear-filters-btn"><X className="h-3 w-3" /> Clear ({activeFilters})</button></>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-[#11141D] border border-white/5 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <Table className="min-w-[1000px]">
            <TableHeader>
              <TableRow className="border-b border-white/5">
                <TableHead className="w-10 text-center">
                  <Checkbox checked={paginated.filter(d => d.status === "Non-Active").length > 0 && paginated.filter(d => d.status === "Non-Active").every(d => selectedIds.has(d.ringba_id))} onCheckedChange={toggleSelectAllPage} className="border-slate-600" data-testid="select-all-checkbox" />
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">DID Number</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Campaign</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Publisher</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Purchase Date</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-center">Calls</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Last Call</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-center">Status</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} className="text-center py-8"><div className="flex items-center justify-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" /></div></TableCell></TableRow>
              ) : paginated.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground" data-testid="no-dids-found">No DIDs found matching your filters</TableCell></TableRow>
              ) : paginated.map((did, idx) => {
                const insight = getInsightLabel(did.purchase_date, did.total_calls, did.status);
                const isSelected = selectedIds.has(did.ringba_id);
                return (
                  <TableRow key={did.did_number + idx} className={`group border-b border-white/5 hover:bg-white/[0.02] transition-colors ${isSelected ? "bg-red-500/5" : ""}`} data-testid={`did-row-${did.did_number}`}>
                    <TableCell className="text-center">
                      {did.status === "Non-Active" ? (
                        <Checkbox checked={isSelected} onCheckedChange={() => toggleSelect(did.ringba_id)} className="border-slate-600" data-testid={`select-${did.did_number}`} />
                      ) : <span className="text-slate-800">—</span>}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-sm">{did.did_number}</span>
                        <CopyBtn text={did.did_number} label="DID" />
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5 max-w-[180px]">
                        <span className="text-sm truncate">{did.campaign_name || "N/A"}</span>
                        {did.campaign_name && <CopyBtn text={did.campaign_name} label="Campaign" />}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5 max-w-[140px]">
                        <span className="text-sm truncate text-muted-foreground">{did.publisher_name || "N/A"}</span>
                        {did.publisher_name && did.publisher_name !== "N/A" && <CopyBtn text={did.publisher_name} label="Publisher" />}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      <div className="flex items-center gap-1.5">
                        <span className="text-muted-foreground">{formatDate(did.purchase_date)}</span>
                        {insight && <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${insight.color}`}>{insight.label}</Badge>}
                      </div>
                    </TableCell>
                    <TableCell className="text-center text-sm font-medium">
                      {did.total_calls > 0 ? did.total_calls : did.ringba_active ? <span className="text-slate-500" title="Active via Ringba (calls under 90s not imported)">—</span> : "0"}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{formatDate(did.last_call_date)}</TableCell>
                    <TableCell className="text-center">
                      {did.status === "Active" ? (
                        <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/25" data-testid="status-active"><CheckCircle2 className="h-3 w-3 mr-1" /> Active</Badge>
                      ) : (
                        <Badge className="bg-red-500/15 text-red-400 border-red-500/25" data-testid="status-nonactive"><XCircle className="h-3 w-3 mr-1" /> Non-Active</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => copyRowInfo(did)} title="Copy all info" className="text-xs text-slate-500 hover:text-blue-400 rounded px-1.5 py-1 transition-all" data-testid={`copy-row-${did.did_number}`}>
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => { setSelectedDid(did); setDetailOpen(true); }} className="text-xs text-slate-400 hover:text-white hover:bg-white/5 rounded px-2 py-1 transition-all" data-testid={`view-did-${did.did_number}`}>Details</button>
                        <button onClick={() => confirmSingleDelete(did)} title="Delete from Ringba" className="text-xs text-slate-600 hover:text-red-400 rounded px-1.5 py-1 transition-all" data-testid={`delete-did-${did.did_number}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-500">Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, filtered.length)} of {filtered.length}</span>
          <div className="flex gap-1">
            <button disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)} className="px-3 py-1.5 text-xs rounded-md border border-white/10 text-slate-400 hover:text-white hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition-all">Prev</button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let page;
              if (totalPages <= 5) page = i + 1;
              else if (currentPage <= 3) page = i + 1;
              else if (currentPage >= totalPages - 2) page = totalPages - 4 + i;
              else page = currentPage - 2 + i;
              return (
                <button key={page} onClick={() => setCurrentPage(page)} className={`w-8 py-1.5 text-xs rounded-md border transition-all ${page === currentPage ? "bg-blue-600/20 border-blue-500/30 text-blue-400" : "border-white/10 text-slate-400 hover:text-white hover:bg-white/5"}`}>{page}</button>
              );
            })}
            <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)} className="px-3 py-1.5 text-xs rounded-md border border-white/10 text-slate-400 hover:text-white hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition-all">Next</button>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-lg" data-testid="did-detail-modal" aria-describedby="did-detail-desc">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Phone className="h-5 w-5 text-blue-500" /> DID Details</DialogTitle>
          </DialogHeader>
          <p id="did-detail-desc" className="sr-only">Detailed information about the selected DID number</p>
          {selectedDid && (
            <div className="space-y-4 mt-2">
              <div className="flex items-center justify-between">
                <div className="group flex items-center gap-2">
                  <span className="text-lg font-mono font-bold">{selectedDid.did_number}</span>
                  <CopyBtn text={selectedDid.did_number} label="DID Number" />
                  {selectedDid.ringba_id && <CopyBtn text={selectedDid.ringba_id} label={`Ringba Number ID ${selectedDid.ringba_id}`} />}
                </div>
                {selectedDid.status === "Active" ? (
                  <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/25"><CheckCircle2 className="h-3 w-3 mr-1" /> Active</Badge>
                ) : (
                  <Badge className="bg-red-500/15 text-red-400 border-red-500/25"><XCircle className="h-3 w-3 mr-1" /> Non-Active</Badge>
                )}
              </div>
              <Separator className="bg-white/5" />
              <div className="grid grid-cols-2 gap-y-3 text-sm">
                <span className="text-slate-500">Campaign</span>
                <div className="group flex items-center gap-1.5"><span className="font-medium">{selectedDid.campaign_name || "N/A"}</span>{selectedDid.campaign_name && <CopyBtn text={selectedDid.campaign_name} label="Campaign" />}</div>
                <span className="text-slate-500">Publisher</span>
                <div className="group flex items-center gap-1.5"><span className="font-medium">{selectedDid.publisher_name || "N/A"}</span>{selectedDid.publisher_name && selectedDid.publisher_name !== "N/A" && <CopyBtn text={selectedDid.publisher_name} label="Publisher" />}</div>
                <span className="text-slate-500">Purchase Date</span><span>{formatDate(selectedDid.purchase_date)}</span>
                <span className="text-slate-500">Age</span><span>{daysSince(selectedDid.purchase_date) !== null ? `${daysSince(selectedDid.purchase_date)} days` : "N/A"}</span>
                <span className="text-slate-500">Total Calls</span><span className="font-medium">{selectedDid.total_calls}</span>
                <span className="text-slate-500">Last Call</span><span>{formatDate(selectedDid.last_call_date)}</span>
                <span className="text-slate-500">Provider</span><span>{selectedDid.provider || "N/A"}</span>
                <span className="text-slate-500">Toll-Free</span><span>{selectedDid.is_toll_free ? "Yes" : "No"}</span>
                <span className="text-slate-500">Auto-Renew</span><span>{selectedDid.auto_renew ? "Yes" : "No"}</span>
                {selectedDid.next_charge_date && !selectedDid.next_charge_date.startsWith("0001") && (
                  <><span className="text-slate-500">Next Charge</span><span>{formatDate(selectedDid.next_charge_date)}</span></>
                )}
              </div>
              {(() => {
                const ins = getInsightLabel(selectedDid.purchase_date, selectedDid.total_calls, selectedDid.status);
                return ins ? (
                  <><Separator className="bg-white/5" /><div className="flex items-center gap-2">
                    <Badge variant="outline" className={ins.color}>{ins.label}</Badge>
                    <span className="text-xs text-slate-500">
                      {ins.label === "New" && "Added less than 7 days ago"}
                      {ins.label === "Old & Dead" && "Over 30 days old with zero calls"}
                      {ins.label === "Idle" && "7-30 days old with no call activity"}
                      {ins.label === "Healthy" && "Active with call traffic"}
                      {ins.label === "Low Activity" && "Ringba shows activity but no analyzed calls"}
                      {ins.label === "No Activity" && "No call activity detected"}
                    </span>
                  </div></>
                ) : null;
              })()}
              <Separator className="bg-white/5" />
              <div className="flex gap-2">
                <button onClick={() => copyRowInfo(selectedDid)} className="flex-1 flex items-center justify-center gap-1.5 text-xs text-slate-300 hover:text-white border border-white/10 hover:bg-white/5 rounded-md py-2 transition-all" data-testid="modal-copy-all">
                  <Copy className="h-3.5 w-3.5" /> Copy All Info
                </button>
                <button onClick={() => { setDetailOpen(false); confirmSingleDelete(selectedDid); }} className="flex-1 flex items-center justify-center gap-1.5 text-xs text-red-400 hover:text-red-300 border border-red-500/20 hover:bg-red-500/10 rounded-md py-2 transition-all" data-testid="modal-delete">
                  <Trash2 className="h-3.5 w-3.5" /> Delete from Ringba
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-md" aria-describedby="delete-confirm-desc">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="h-5 w-5" />
              {deleteTarget === "bulk" ? "Bulk Delete DIDs" : "Delete DID"}
            </DialogTitle>
          </DialogHeader>
          <p id="delete-confirm-desc" className="sr-only">Confirm DID deletion</p>
          <div className="space-y-3 mt-2">
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-sm text-red-300">
              This will permanently deallocate {deleteTarget === "bulk" ? `${selectedIds.size} number${selectedIds.size > 1 ? "s" : ""}` : "this number"} from your Ringba account. This action cannot be undone.
            </div>
            <div className="bg-amber-500/5 border border-amber-500/15 rounded-lg p-2.5 text-xs text-amber-300/80">
              Note: If a number is still assigned to a campaign in Ringba, you'll need to unassign it from Ringba Dashboard first (Numbers → Manage Numbers → remove campaign assignment).
            </div>
            {deleteTarget && deleteTarget !== "bulk" && (
              <div className="text-sm">
                <span className="text-slate-500">Number: </span>
                <span className="font-mono font-medium">{deleteTarget.did_number}</span>
                <br />
                <span className="text-slate-500">Campaign: </span>
                <span>{deleteTarget.campaign_name}</span>
              </div>
            )}
            {deleteTarget === "bulk" && (
              <div className="max-h-32 overflow-y-auto text-xs text-slate-400 space-y-0.5">
                {bulkSelectedDids.map(d => (
                  <div key={d.ringba_id} className="font-mono">{d.did_number} — {d.campaign_name}</div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter className="mt-4 gap-2">
            <button onClick={() => setDeleteConfirmOpen(false)} className="px-4 py-2 text-sm text-slate-400 hover:text-white border border-white/10 rounded-md transition-all">Cancel</button>
            <button onClick={executeDelete} disabled={deleting} data-testid="confirm-delete-btn" className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-500 rounded-md transition-all disabled:opacity-50">
              {deleting ? "Deleting..." : `Delete ${deleteTarget === "bulk" ? selectedIds.size + " DID" + (selectedIds.size > 1 ? "s" : "") : "DID"}`}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deletion Log Modal */}
      <Dialog open={deletionLogOpen} onOpenChange={setDeletionLogOpen}>
        <DialogContent className="max-w-lg max-h-[80vh]" aria-describedby="deletion-log-desc">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History className="h-5 w-5 text-slate-400" /> Deletion Log</DialogTitle>
          </DialogHeader>
          <p id="deletion-log-desc" className="sr-only">History of deleted DID numbers</p>
          <div className="overflow-y-auto max-h-[50vh] space-y-2 mt-2">
            {deletionLogs.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6">No deletions recorded yet</p>
            ) : deletionLogs.map((log, i) => (
              <div key={i} className={`text-xs p-2.5 rounded-lg border ${log.status === "success" ? "bg-emerald-500/5 border-emerald-500/15" : "bg-red-500/5 border-red-500/15"}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-mono text-slate-300">{log.number_id?.slice(0, 20)}...</span>
                  <Badge variant="outline" className={log.status === "success" ? "text-emerald-400 border-emerald-500/30 text-[10px]" : "text-red-400 border-red-500/30 text-[10px]"}>{log.status}</Badge>
                </div>
                <div className="text-slate-500">{formatDate(log.deleted_at)} {log.bulk && "• Bulk"}</div>
                {log.error && <div className="text-red-400 mt-1 truncate">{log.error}</div>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
